// Недостигът в записа на листа „Изписване": пречката във футъра, която
// назовава кое не стига („Недостатъчна наличност: Домати — нужни 1,200 кг,
// налични 0,500 кг (за Шопска салата ×2); …"), и въпросът при Ctrl+Enter,
// когато не стига съставка на неразделена рецепта (WriteoffShortageDialog) —
// за първия такъв ред по реда на документа. Изборът „Намали" / „Раздели"
// поправя реда и връща фокуса там, откъдето човек продължава: порциите на
// реда или количеството на първата недостигаща съставка след разделянето.
// Автоматичният брак от Маркиране идва тук без недостиг (`skipStockCheck` на
// useWriteoffRowsActions — не се проверява, както на BE).
import { useRef, useState } from "react";
import type { TFunction } from "i18next";
import type { RecipeRowStock } from "./useWriteoffStock";
import { WriteoffShortageDialog } from "./WriteoffShortageDialog";
import { shortageIssueText, type StockShortage } from "./writeoffStockCheck";
import type { WriteoffLineDraft } from "./writeoffWorksheetShared";

type Params = {
  t: TFunction;
  lines: WriteoffLineDraft[];
  shortages: StockShortage[];
  recipeStockByKey: Map<number, RecipeRowStock>;
  dateLabel: string;
  warehouseName: string;
  setRecipePortions: (key: number, portions: number) => void;
  splitRecipeRow: (key: number, options?: { askFirst?: boolean }) => Promise<WriteoffLineDraft[] | null>;
  focusCell: (lineKey: number, col: number) => void;
};

// Редът, за който е въпросът — снимка при отварянето (след „Раздели" редът
// вече го няма, а съдържанието стои до края на анимацията)
type DialogState = { line: WriteoffLineDraft; stock: RecipeRowStock | undefined; open: boolean };

export function useWriteoffShortageFlow({
  t,
  lines,
  shortages,
  recipeStockByKey,
  dateLabel,
  warehouseName,
  setRecipePortions,
  splitRecipeRow,
  focusCell,
}: Params) {
  const [dialog, setDialog] = useState<DialogState | null>(null);
  // какво да се фокусира СЛЕД затварянето (antd иначе връща фокуса назад)
  const afterCloseRef = useRef<(() => void) | null>(null);

  const issue = shortages.length ? shortageIssueText(t, shortages) : null;

  // Ctrl+Enter: първият ред-рецепта с недостигаща съставка → въпросът.
  // true = записът е поет тук (иначе листът показва пречката както досега).
  const interceptSubmit = (): boolean => {
    const recipeLine = lines.find(
      (line) => line.kind === "recipe" && (recipeStockByKey.get(line.key)?.shortages.length ?? 0) > 0,
    );
    if (!recipeLine) return false;
    setDialog({ line: recipeLine, stock: recipeStockByKey.get(recipeLine.key), open: true });
    return true;
  };

  const closeThen = (next: () => void) => {
    afterCloseRef.current = next;
    setDialog((current) => (current ? { ...current, open: false } : current));
  };

  const lineKey = dialog?.line.key ?? null;
  const rowStock = dialog?.stock;

  const element = (
    <WriteoffShortageDialog
      open={dialog?.open ?? false}
      line={dialog?.line ?? null}
      stock={rowStock}
      dateLabel={dateLabel}
      warehouseName={warehouseName}
      onFit={(portions) => {
        if (lineKey == null) return;
        setRecipePortions(lineKey, portions);
        closeThen(() => focusCell(lineKey, 0));
      }}
      onSplit={async () => {
        if (lineKey == null) return;
        const shortProductIds = new Set(rowStock?.shortages.map((shortage) => shortage.productId));
        const rows = await splitRecipeRow(lineKey, { askFirst: false });
        const target =
          rows?.find((row) => row.productId != null && shortProductIds.has(row.productId)) ?? rows?.[0];
        closeThen(() => focusCell(target ? target.key : lineKey, 0));
      }}
      onCancel={() => {
        if (lineKey == null) return;
        closeThen(() => focusCell(lineKey, 0));
      }}
      afterClose={() => {
        const next = afterCloseRef.current;
        afterCloseRef.current = null;
        setDialog(null);
        next?.();
      }}
    />
  );

  return { issue, interceptSubmit, dialog: element };
}
